//https://github.com/remirror/remirror/tree/main/packages/remirror__extension-tables/src
//https://github.com/ProseMirror/prosemirror-tables

import {
  TableCellExtension,
  TableControllerCellExtension,
  TableExtension,
  TableHeaderCellExtension,
  TableSchemaSpec,
} from "remirror/extensions";

import {
  extension,
  ExtensionPriority,
  NodeExtension,
  ApplySchemaAttributes,
  ExtensionTag,
  NodeSpecOverride,
  ProsemirrorNode,
  ProsemirrorPlugin,
  isElementDomNode,
  command,
  findParentNodeOfType,
  convertCommand,
} from "remirror";

import { removeStyleAttribute } from "./extension-utils";

import type { CommandFunction, EditorState, Transaction } from "remirror";

import {
  setCellAttr,
  mergeCells,
  splitCell,
  toggleHeader,
  TableMap,
  TableView,
  columnResizing,
  tableEditing,
} from "@remirror/pm/tables";

import { Decoration, DecorationSet } from "@remirror/pm/view";
import type { EditorView } from "@remirror/pm/view";

const borderSideNames = ["top", "right", "bottom", "left"] as const;

// Styles a product list writes on its cells from the Design tab. Carried as attrs,
// or the first inline edit drops them (B422).
const cellStyleAttrs = [
  ["verticalAlign", "vertical-align"],
  ["padding", "padding"],
  ["fontSize", "font-size"],
  ["fontFamily", "font-family"],
] as const;

// A product list with "Bottom" cell borders writes border-bottom-* only. Keep which
// sides are drawn, or the round trip turns it into a full grid. null = all four.
function getBorderSides(dom: HTMLElement): string | null {
  const sides = borderSideNames.filter((side) => {
    const style = dom.style.getPropertyValue(`border-${side}-style`);
    const width = dom.style.getPropertyValue(`border-${side}-width`);
    return style && style !== "none" && style !== "hidden" && width !== "0px";
  });
  return sides.length > 0 && sides.length < 4 ? sides.join(" ") : null;
}

const hasFullWidth = (style?: string | null) =>
  /(^|;)\s*width:\s*100%/.test(style ?? "");

// The resize plugin draws its own <table> without the node's style, so a product list
// (width: 100% in the output) showed at content width while editing and the drag
// gave widths unrelated to the page. The class keeps it 100% wide (designer.scss).
export class CmTableView extends TableView {
  constructor(node: ProsemirrorNode, cellMinWidth: number) {
    super(node, cellMinWidth);
    this.markFullWidth(node);
  }

  update(node: ProsemirrorNode) {
    const updated = super.update(node);
    if (updated) this.markFullWidth(node);
    return updated;
  }

  private markFullWidth(node: ProsemirrorNode) {
    this.table.classList.toggle("cm-table-full", hasFullWidth(node.attrs.style));
  }
}

type ColumnWidth = { px?: number | null; pct?: number | null };

// A dragged column comes back as px. Mixed px and % columns lay out unpredictably, and
// px does not follow the page or PDF width, so the table is turned into % only: px
// columns keep their share of the table, the others split the rest in their ratio.
export function toPercentWidths(cols: ColumnWidth[], tableWidth: number) {
  const fixed = cols.map((c) => (c.px ? (c.px / tableWidth) * 100 : null));
  const flex = cols.filter((_, i) => fixed[i] == null);
  const known = flex.filter((c) => c.pct).map((c) => c.pct as number);
  const fallback = known.length
    ? known.reduce((a, b) => a + b, 0) / known.length
    : 1;
  const flexPct = cols.map((c, i) => (fixed[i] == null ? c.pct || fallback : 0));
  let fixedSum = fixed.reduce<number>((a, b) => a + (b ?? 0), 0);
  const cap = flex.length ? 90 : 100;
  const fixedScale = fixedSum > cap || !flex.length ? cap / fixedSum : 1;
  fixedSum *= fixedScale;
  const flexSum = flexPct.reduce((a, b) => a + b, 0);
  return cols.map((_, i) =>
    fixed[i] != null
      ? Math.round((fixed[i] as number) * fixedScale * 10) / 10
      : Math.round(((flexPct[i] / flexSum) * (100 - fixedSum)) * 10) / 10
  );
}

const pxOf = (width?: string | null) =>
  width && /^[\d.]+px$/.test(width) ? parseFloat(width) : null;
const pctOf = (width?: string | null) =>
  width && /^[\d.]+%$/.test(width) ? parseFloat(width) : null;

// Rewrites a product list table holding a px width (a drag, or one saved before this fix) to %.
// Widths go on the first row only, as the generator writes them.
function percentTables(state: EditorState, view: EditorView | null) {
  if (!view) return null;
  let tr: Transaction | null = null;
  state.doc.descendants((table, pos) => {
    if (table.type.name !== "table") return true;
    // Product lists only (width: 100%); a hand-made table keeps its px widths
    if (!hasFullWidth(table.attrs.style)) return false;
    let hasPx = false;
    table.descendants((cell) => {
      if (cell.attrs.colwidth || pxOf(cell.attrs.width)) hasPx = true;
      return !hasPx && cell.type.name === "tableRow";
    });
    if (!hasPx) return false;
    const dom = view.nodeDOM(pos) as HTMLElement | null;
    const el = dom?.tagName === "TABLE" ? dom : dom?.querySelector?.("table");
    const tableWidth = el?.offsetWidth;
    if (!tableWidth) return false;

    const map = TableMap.get(table);
    const cols: ColumnWidth[] = [];
    for (let col = 0; col < map.width; col++) {
      const cellPos = map.map[col];
      const attrs = table.nodeAt(cellPos)!.attrs;
      const index = attrs.colspan === 1 ? 0 : col - map.colCount(cellPos);
      cols.push({
        px: attrs.colwidth?.[index] || pxOf(attrs.width),
        pct: pctOf(attrs.width),
      });
    }
    const pcts = toPercentWidths(cols, tableWidth);

    tr ??= state.tr;
    for (const cellPos of new Set(map.map)) {
      const cell = table.nodeAt(cellPos)!;
      const start = map.colCount(cellPos);
      const firstRow = map.map.indexOf(cellPos) < map.width;
      const share = pcts
        .slice(start, start + cell.attrs.colspan)
        .reduce((a, b) => a + b, 0);
      tr.setNodeMarkup(tr.mapping.map(pos + 1 + cellPos), undefined, {
        ...cell.attrs,
        colwidth: null,
        width: firstRow ? `${Math.round(share * 10) / 10}%` : null,
      });
    }
    return false;
  });
  return tr;
}

function withoutBorder(style?: string | null) {
  return (style ?? "")
    .split(";")
    .map((decl) => decl.trim())
    .filter((decl) => decl && !/^border(-[a-z-]+)?\s*:/i.test(decl))
    .map((decl) => `${decl}; `)
    .join("");
}

function getCellAttrs(dom: HTMLElement) {
  const borderSides = getBorderSides(dom);
  return {
    colspan: Number(dom.getAttribute("colspan") ?? 1),
    rowspan: Number(dom.getAttribute("rowspan") ?? 1),
    background: dom.style.backgroundColor || null,
    textAlign: dom.style.textAlign || null,
    // borderColor reads "" when the sides differ, e.g. after "border-bottom: 1px solid #ccc"
    borderColor:
      dom.style.borderColor ||
      (borderSides &&
        dom.style.getPropertyValue(
          `border-${borderSides.split(" ")[0]}-color`
        )) ||
      null,
    borderSides,
    // A product list header writes "width:16.7%"; colwidth only knows px (B424)
    width: dom.style.width || null,
    ...Object.fromEntries(
      cellStyleAttrs.map(([attr, css]) => [
        attr,
        dom.style.getPropertyValue(css) || null,
      ])
    ),
  };
}

function setCellAttrs(node: ProsemirrorNode) {
  const attrs: Record<string, string> = {};

  if (node.attrs.colspan !== 1) {
    attrs.colspan = node.attrs.colspan;
  }

  if (node.attrs.rowspan !== 1) {
    attrs.rowspan = node.attrs.rowspan;
  }
  if (node.attrs.colwidth) {
    const width = node.attrs.colwidth;
    attrs.style = `width: ${width}px;${removeStyleAttribute(
      node.attrs.style,
      "width"
    )}`;
  } else if (node.attrs.width) {
    attrs.style = `width: ${node.attrs.width};`;
  }

  if (node.attrs.background) {
    attrs.style =
      (attrs.style || "") + `background-color: ${node.attrs.background};`;
  }

  if (node.attrs.textAlign) {
    attrs.style = (attrs.style || "") + `text-align: ${node.attrs.textAlign};`;
  }

  for (const [attr, css] of cellStyleAttrs) {
    if (node.attrs[attr]) {
      attrs.style = (attrs.style || "") + `${css}: ${node.attrs[attr]};`;
    }
  }

  if (node.attrs.borderColor) {
    const border = `1px solid ${node.attrs.borderColor};`;
    attrs.style =
      (attrs.style || "") +
      (node.attrs.borderSides
        ? (node.attrs.borderSides as string)
            .split(" ")
            .map((side) => `border-${side}: ${border}`)
            .join(" ")
        : `border: ${border}`);
  }

  return attrs;
}

export function createTableNodeSchema(
  extra: ApplySchemaAttributes,
  override: NodeSpecOverride
): Record<
  "table" | "tableRow" | "tableCell" | "tableHeaderCell",
  TableSchemaSpec
> {
  const cellAttrs = {
    ...extra.defaults(),
    colspan: { default: 1 },
    rowspan: { default: 1 },
    colwidth: { default: null },
    width: { default: null },
    background: { default: null },
    textAlign: { default: null },
    borderColor: { default: null },
    borderSides: { default: null },
    ...Object.fromEntries(
      cellStyleAttrs.map(([attr]) => [attr, { default: null }])
    ),
  };

  return {
    table: {
      isolating: true,
      ...override,
      attrs: {
        ...extra.defaults(),
        border: { default: null },
      },
      content: "tableRow+",
      tableRole: "table",
      parseDOM: [
        {
          tag: "table",
          getAttrs: (dom) => {
            if (!isElementDomNode(dom)) {
              return false;
            }
            const attrs = {
              ...extra.parse(dom),
              border: dom.getAttribute("border"),
            };
            return attrs;
          },
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM(node) {
        const attrs = extra.dom(node);
        if (node.attrs.border) {
          attrs.border = node.attrs.border;
          // Replace only the border: a Bottom / None product list writes
          // style="width: 100%;" here, and losing it shrinks the table to its content.
          attrs.style = `${withoutBorder(attrs.style as string)}border: ${
            node.attrs.border === "1" ? "1px solid black" : "none"
          };`;
        }
        return ["table", attrs, ["tbody", 0]];
      },
    },

    tableRow: {
      ...override,
      attrs: extra.defaults(),
      content: "(tableCell | tableHeaderCell)*",
      tableRole: "row",
      parseDOM: [
        {
          tag: "tr",
          getAttrs: (dom) => ({
            ...extra.parse(dom),
          }),
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM(node) {
        return ["tr", extra.dom(node), 0];
      },
    },

    tableCell: {
      isolating: true,
      content: `${ExtensionTag.Block}+`,
      ...override,
      attrs: cellAttrs,
      tableRole: "cell",
      parseDOM: [
        {
          tag: "td",
          getAttrs: (dom) => ({
            ...extra.parse(dom),
            ...getCellAttrs(dom as HTMLElement),
          }),
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM(node) {
        return ["td", { ...extra.dom(node), ...setCellAttrs(node) }, 0];
      },
    },

    tableHeaderCell: {
      isolating: true,
      content: `${ExtensionTag.Block}+`,
      ...override,
      attrs: cellAttrs,
      tableRole: "header_cell",
      parseDOM: [
        {
          tag: "th",
          getAttrs: (dom) => ({
            ...extra.parse(dom),
            ...getCellAttrs(dom as HTMLElement),
          }),
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM(node) {
        return ["th", { ...extra.dom(node), ...setCellAttrs(node) }, 0];
      },
    },
  };
}

@extension({ defaultOptions: {}, defaultPriority: ExtensionPriority.Low })
export class CmTableRowExtension extends NodeExtension {
  get name() {
    return "tableRow" as const;
  }

  createExtensions(): Array<
    TableCellExtension | TableHeaderCellExtension | TableControllerCellExtension
  > {
    return [
      new CmTableCellExtension({ priority: ExtensionPriority.Low }),
      new CmTableHeaderCellExtension({ priority: ExtensionPriority.Low }),
    ];
  }

  createNodeSpec(
    extra: ApplySchemaAttributes,
    override: NodeSpecOverride
  ): TableSchemaSpec {
    return createTableNodeSchema(extra, override).tableRow;
  }

  createExternalPlugins(): ProsemirrorPlugin[] {
    const plugins: ProsemirrorPlugin[] = [];

    return plugins;
  }
}

@extension({ defaultOptions: {}, defaultPriority: ExtensionPriority.Low })
export class CmTableCellExtension extends NodeExtension {
  get name() {
    return "tableCell" as const;
  }

  createNodeSpec(
    extra: ApplySchemaAttributes,
    override: NodeSpecOverride
  ): TableSchemaSpec {
    return createTableNodeSchema(extra, override).tableCell;
  }
}

@extension({ defaultOptions: {}, defaultPriority: ExtensionPriority.Low })
export class CmTableHeaderCellExtension extends NodeExtension {
  get name() {
    return "tableHeaderCell" as const;
  }

  createNodeSpec(
    extra: ApplySchemaAttributes,
    override: NodeSpecOverride
  ): TableSchemaSpec {
    return createTableNodeSchema(extra, override).tableHeaderCell;
  }
}

export class CmTableExtension extends TableExtension {
  createNodeSpec(extra, override) {
    return createTableNodeSchema(extra, override).table;
  }

  // As upstream, with CmTableView drawing the table while editing.
  createExternalPlugins(): ProsemirrorPlugin[] {
    if (this.store.isMounted() && !this.store.helpers.isViewEditable()) {
      return [];
    }
    return [
      columnResizing({ ...this.options.resizeableOptions, View: CmTableView }),
      tableEditing(),
    ];
  }

  createExtensions() {
    return [new CmTableRowExtension({ priority: ExtensionPriority.Low })];
  }

  createPlugin() {
    let editorView: EditorView | null = null;
    return {
      view: (view: EditorView) => {
        editorView = view;
        return {};
      },
      appendTransaction: (
        transactions: readonly Transaction[],
        _old: EditorState,
        state: EditorState
      ) =>
        transactions.some((t) => t.docChanged)
          ? percentTables(state, editorView)
          : null,
      props: {
        decorations: (state: EditorState) => {
          const decorations: Decoration[] = [];

          state.doc.descendants((node, pos) => {
            if (node.type.name === "table" && node.attrs.border === "0") {
              const decoration = Decoration.node(pos, pos + node.nodeSize, {
                class: "table-no-border",
              });

              decorations.push(decoration);
            }
          });

          // Create and return the DecorationSet
          return DecorationSet.create(state.doc, decorations);
        },
      },
    };
  }

  @command()
  toggleTableBorder(): CommandFunction {
    return ({ tr, dispatch }) => {
      const tableNode = findParentNodeOfType({
        types: this.type,
        selection: tr.selection,
      });

      if (!tableNode) return false;

      const currentBorder = tableNode.node.attrs.border;
      const newAttrs = {
        ...tableNode.node.attrs,
        border: currentBorder !== "0" ? "0" : "1",
      };

      if (dispatch) {
        dispatch(tr.setNodeMarkup(tableNode.pos, undefined, newAttrs));
        return true;
      }
      return false;
    };
  }

  @command()
  setTableCellBackground(background: string | null): CommandFunction {
    return convertCommand(setCellAttr("background", background));
  }

  @command()
  clearTableCellBackground(): CommandFunction {
    return convertCommand(setCellAttr("background", null));
  }

  @command()
  setTableCellTextAlign(
    align: "left" | "center" | "right" | null
  ): CommandFunction {
    return convertCommand(setCellAttr("textAlign", align));
  }

  @command()
  setTableCellBorderColor(color: string | null): CommandFunction {
    return convertCommand(setCellAttr("borderColor", color));
  }

  @command()
  clearTableCellBorderColor(): CommandFunction {
    return convertCommand(setCellAttr("borderColor", null));
  }

  @command()
  mergeTableCells(): CommandFunction {
    return convertCommand(mergeCells);
  }

  @command()
  splitTableCell(): CommandFunction {
    return convertCommand(splitCell);
  }

  @command()
  toggleTableHeaderRow(): CommandFunction {
    return convertCommand(toggleHeader("row"));
  }

  @command()
  toggleTableHeaderColumn(): CommandFunction {
    return convertCommand(toggleHeader("column"));
  }
}
